"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Coins,
  FileText,
  KeyRound,
  Layers,
  Lightbulb,
  Save,
  Sparkles,
  Trash2,
  Upload,
  User,
  Wand2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { ExperimentalBadge } from "@/components/experimental-badge";
import { getUser } from "@/functions/get-user";
import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from "@super-resume-new/ui/components/dialog";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@super-resume-new/ui/components/tabs";
import { Textarea } from "@super-resume-new/ui/components/textarea";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/account")({
  beforeLoad: async () => {
    const session = await getUser();
    if (!session) {
      throw redirect({ to: "/" });
    }
    return { session };
  },
  head: () =>
    seo({
      title: "Master Career Profile & Account Settings | JobCues",
      description:
        "Manage your central Master Career Profile repository, evidence-based skills, and connected platform integrations.",
      path: "/account",
      noindex: true,
    }),
  component: AccountPageComponent,
});

const SKELETON_TEMPLATE = `# CONTACT INFORMATION
Name: [Your Full Name]
Email: [Your Email]
Phone: [Your Phone]
Location: [City, State / Country]
LinkedIn: [linkedin.com/in/yourprofile]
GitHub / Portfolio: [github.com/yourhandle]

# SUMMARY (Optional)
[Optional 1-2 sentence career positioning statement or status]

# TECHNICAL & CORE SKILLS
Categorize ALL skills you know. Don't worry about length — the AI will pick the relevant ones for each job.
• Languages: TypeScript, JavaScript, Python, Go, SQL, HTML, CSS, Rust, Java, C#
• Frameworks & Libraries: React, Next.js, Node.js, Express, FastAPI, Django, Tailwind CSS, Vue.js, GraphQL
• Cloud & Infrastructure: AWS (EC2, S3, Lambda), GCP, Docker, Kubernetes, CI/CD, Terraform, Cloudflare
• Databases: PostgreSQL, MongoDB, Redis, MySQL, SQLite, Supabase, DynamoDB
• Tools & Methodologies: Git, GitHub, Agile, Scrum, Jest, Cypress, REST APIs, Microservices, System Design

# WORK EXPERIENCE
List all your past roles and every achievement/bullet you have. Include detailed technical context and human business outcomes.

[Senior Software Engineer] | [Acme Corp] | [San Francisco, CA] | [Jan 2023 - Present]
• Built and launched a high-throughput customer billing portal with React, TypeScript, Node.js, and PostgreSQL, reducing payment failure rates by 35% for 50,000+ monthly active users.
• Architected automated CI/CD deployment pipelines using Docker, GitHub Actions, and AWS, cutting build and deployment cycle times from 45 minutes to under 6 minutes.
• Mentored 4 junior engineers through pair programming and structured code reviews in an Agile Scrum team, improving team code delivery velocity by 20%.
• Designed RESTful APIs and background job queues with Node.js and Redis, preventing server timeouts during peak month-end transaction spikes.

[Software Engineer] | [TechCorp Inc.] | [New York, NY] | [Jun 2021 - Dec 2022]
• Developed real-time dashboard visualizations with React, TypeScript, and Tailwind CSS, allowing operations teams to monitor warehouse inventory in real time.
• Refactored legacy backend monolith into modular Go microservices over PostgreSQL, reducing API endpoint response latencies by 4x.
• Collaborated with product designers and non-technical stakeholders to translate complex analytics requirements into user-friendly reporting features.

# PROJECTS
Add as many projects as you have built (e.g. 5–20+ projects). The AI will select the 3–4 best matches for each target job.

[Super SaaS Platform] | [https://github.com/yourhandle/saas-app]
• Developed a full-stack multi-tenant SaaS application using Next.js, TypeScript, Tailwind CSS, and Stripe, allowing creators to accept subscriptions with zero setup.
• Integrated PostgreSQL database with Drizzle ORM and Redis caching, ensuring sub-50ms API response times under load.

[AI Workflow Automation Tool] | [https://github.com/yourhandle/ai-tool]
• Built an automated document extraction pipeline using Python, FastAPI, Docker, and LLM APIs, enabling legal teams to summarize contracts in seconds.
• Implemented vector embeddings and similarity search over PostgreSQL (pgvector), achieving 94% retrieval accuracy.

[Real-time Chat Application] | [https://github.com/yourhandle/chat-app]
• Engineered a distributed WebSocket messaging system with Go, Redis pub/sub, and React, supporting 10,000+ simultaneous chat connections with zero message drop.

# EDUCATION
[Bachelor of Science in Computer Science] | [University Name] | [City, State] | [Graduation: May 2021]
• Relevant Coursework: Data Structures, Algorithms, Distributed Systems, Database Management.

# CERTIFICATIONS & ACHIEVEMENTS
• AWS Certified Solutions Architect - Associate (2024)
• Hackathon Winner - 1st Place at Global AI Hackathon (2023)
`;

function AccountPageComponent() {
  const { session } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const DRAFT_KEY = "account-master-draft";

  const [activeTab, setActiveTab] = useState("profile");
  const [masterText, setMasterText] = useState(() => localStorage.getItem(DRAFT_KEY) ?? "");
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const serverSeedDone = useRef(false);

  // Persist draft on every keystroke
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, masterText);
  }, [masterText]);

  // Fetch current user profile
  const { data: profile } = useQuery({
    queryKey: ["user", "profile"],
    queryFn: async () => client.user.getProfile(),
  });

  // Seed from server only once, and only when no local draft exists
  useEffect(() => {
    if (serverSeedDone.current) return;
    if (profile?.masterResumeText === undefined || profile?.masterResumeText === null) return;
    serverSeedDone.current = true;
    if (!localStorage.getItem(DRAFT_KEY)) {
      setMasterText(profile.masterResumeText);
    }
    if (profile.updatedAt) {
      setLastSavedAt(new Date(profile.updatedAt));
    }
  }, [profile]);

  // Mutation to save master resume
  const saveMutation = useMutation({
    mutationFn: async (text: string) => {
      return client.user.updateMasterResume({ masterResumeText: text });
    },
    onSuccess: () => {
      localStorage.removeItem(DRAFT_KEY);
      setLastSavedAt(new Date());
      toast.success("Master Career Profile saved successfully!");
      queryClient.invalidateQueries({ queryKey: ["user", "profile"] });
    },
    onError: (err: any) => {
      toast.error("Failed to save profile: " + (err.message || "Unknown error"));
    },
  });

  // Keyboard shortcut Ctrl+S / Cmd+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        saveMutation.mutate(masterText);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [masterText, saveMutation]);

  // Handle file upload and parsing
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["pdf", "docx", "doc"].includes(ext)) {
      toast.error("Please upload a PDF, DOCX, or DOC resume file.");
      return;
    }

    try {
      setIsParsingFile(true);
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(",")[1] || "";
          const res = await client.user.parseResumeFile({
            file_base64: base64,
            file_type: ext as "pdf" | "docx" | "doc",
          });

          if (res.text && res.text.trim()) {
            setMasterText(res.text.trim());
            toast.success("Resume parsed! Review your text and click Save.");
          } else {
            toast.error("Could not extract text from this file. Please paste your text manually.");
          }
        } catch (err: any) {
          toast.error("Failed to parse file: " + (err.message || "Unknown error"));
        } finally {
          setIsParsingFile(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setIsParsingFile(false);
      toast.error("Failed to read file: " + err.message);
    }
  };

  const handleInsertTemplate = () => {
    if (masterText.trim().length > 50) {
      if (!confirm("This will replace your current text with the template skeleton. Proceed?")) {
        return;
      }
    }
    setMasterText(SKELETON_TEMPLATE.trim());
    toast.info("Template inserted! Edit with your career details.");
  };

  const wordCount = masterText.trim() ? masterText.trim().split(/\s+/).length : 0;
  const lineCount = masterText.trim() ? masterText.split("\n").length : 0;
  const isDirty = profile ? masterText !== (profile.masterResumeText ?? "") : false;

  return (
    <PageLayout id="main-content" as="div" className="max-w-7xl">
      <PageHeader
        title="Account and profile"
        description={session?.user?.email}
        icon={User}
        badge={
          masterText.trim() ? (
            <Badge variant="outline" className="text-[11px]">
              <CheckCircle2 className="mr-1 size-3 text-emerald-500" aria-hidden="true" /> Master
              profile active
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[11px]">
              <AlertCircle className="mr-1 size-3 text-amber-500" aria-hidden="true" /> Profile
              empty
            </Badge>
          )
        }
        actions={
          <Link to="/credits">
            <Button variant="outline" size="sm" className="gap-1.5">
              <Coins className="size-3.5" aria-hidden="true" />
              Credits and activity
              <ArrowRight className="size-3" aria-hidden="true" />
            </Button>
          </Link>
        }
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="gap-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTab value="profile" className="flex items-center gap-2 py-2">
            <Sparkles className="size-4" aria-hidden="true" />
            Master career profile
          </TabsTab>
          <TabsTab value="settings" className="flex items-center gap-2 py-2">
            <User className="size-4" aria-hidden="true" />
            Account details
          </TabsTab>
        </TabsList>

        {/* Tab 1: Master Career Profile */}
        <TabsPanel value="profile" className="flex flex-col gap-4">
          {/* Explanation Banner */}
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex size-7 items-center justify-center rounded-lg bg-primary text-white font-semibold text-xs">
                    <Wand2 className="size-4" />
                  </div>
                  <h2 className="text-sm font-semibold text-foreground">
                    Your Central Master Career Repository
                  </h2>
                </div>

                <Dialog>
                  <DialogTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-xs text-brand-foreground border-brand/30 bg-brand/10 hover:bg-brand/20 font-medium cursor-pointer"
                      >
                        <Lightbulb className="size-3.5 text-muted-foreground" />
                        Quick Tips
                      </Button>
                    }
                  />
                  <DialogPopup>
                    <DialogHeader>
                      <div className="flex items-center gap-2 text-brand-foreground">
                        <DialogTitle>Quick Tips for Best Results</DialogTitle>
                      </div>
                      <DialogDescription>
                        Small details you provide that make a big difference in the output:
                      </DialogDescription>
                    </DialogHeader>
                    <DialogPanel className="space-y-3 text-sm text-foreground">
                      <div className="flex items-start gap-2.5">
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand-foreground font-semibold text-xs mt-0.5">
                          1
                        </span>
                        <div>
                          <p className="font-semibold text-foreground text-base">
                            Name the skills in the actual content
                          </p>
                          <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                            Mention skills you used & learned (e.g. <em>React, Docker, SQL</em>) in
                            your project and experince contents. That way AI does not have to guess
                            and it provides real evidence needed for ATS scoring.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand-foreground font-semibold text-xs mt-0.5">
                          2
                        </span>
                        <div>
                          <p className="font-semibold text-foreground text-base">
                            List all your projects, side builds & job experiences
                          </p>
                          <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                            Having 10+ projects gives AI the variety to cherry-pick the best match
                            for each role.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand-foreground font-semibold text-xs mt-0.5">
                          3
                        </span>
                        <div>
                          <p className="font-semibold text-foreground text-base">
                            Include rough numbers where you can
                          </p>
                          <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                            AI cannot invent real figures. Adding rough estimates (e.g.{" "}
                            <em>&quot;~10k users&quot;</em> or{" "}
                            <em>&quot;cut build time in half&quot;</em>) lets it craft high-impact
                            outcome bullets.
                          </p>
                        </div>
                      </div>
                    </DialogPanel>
                    <DialogFooter>
                      <DialogClose render={<Button variant="outline">Got it</Button>} />
                    </DialogFooter>
                  </DialogPopup>
                </Dialog>
              </div>
              <p className="text-muted-foreground">
                This is your comprehensive career archive. Unlike a traditional 1-page resume, we
                encourage you to enter{" "}
                <strong className="font-semibold text-foreground">
                  everything you have ever done
                </strong>{" "}
                e.g. all 20+ projects you did, list every skills you used, everything from your
                previous experiences, etc.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                <div className="flex flex-col gap-1 rounded-xl border border-border bg-card/60 p-3">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Layers className="size-3.5 text-muted-foreground" />
                    1. Enter Everything
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    List all your past projects, comprehensive skills, and all work experience
                    bullets without worrying about page limits.
                  </p>
                </div>

                <div className="flex flex-col gap-1 rounded-xl border border-border bg-card/60 p-3">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Sparkles className="size-3.5 text-muted-foreground" />
                    2. AI Intelligent Selection
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    When you tailor for a job, the AI cherry-picks the most impactful and relevant
                    things matching the job requirements.
                  </p>
                </div>

                <div className="flex flex-col gap-1 rounded-xl border border-border bg-card/60 p-3">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Briefcase className="size-3.5 text-muted-foreground" />
                    3. Standard Resume Output
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    Always keeps education and core history while trimming off-target clutter into a
                    tight, ATS-optimized resume.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Editor Container */}
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-sm">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.docx,.doc"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  loading={isParsingFile}
                  className="text-xs font-medium"
                >
                  <Upload className="mr-1.5 size-3.5 text-muted-foreground" />
                  Upload &amp; Parse Resume
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleInsertTemplate}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  <FileText className="mr-1.5 size-3.5" />
                  Insert Template Structure
                </Button>

                {masterText.trim() && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm("Are you sure you want to clear your Master Profile?")) {
                        localStorage.removeItem(DRAFT_KEY);
                        setMasterText("");
                      }
                    }}
                    className="text-xs font-medium text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="mr-1 size-3.5" />
                    Clear
                  </Button>
                )}
              </div>

              {/* Live counts and dirty status */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{wordCount} words</span>
                <span>•</span>
                <span>{lineCount} lines</span>
                <span>•</span>
                {isDirty ? (
                  <Badge
                    variant="outline"
                    className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[11px]"
                  >
                    Unsaved changes
                  </Badge>
                ) : lastSavedAt ? (
                  <>
                    <span>
                      Last saved{" "}
                      {new Intl.DateTimeFormat("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      }).format(lastSavedAt)}
                    </span>
                    <span className="text-emerald-500 flex items-center gap-1 font-medium">
                      <CheckCircle2 className="size-3" /> Saved
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            {/* Big Textarea */}
            <div className="flex flex-col gap-1.5">
              <Textarea
                id="master-resume-textarea"
                placeholder="Paste or upload your complete resume / career background here… Include all projects, detailed bullet points, technical skills, education, and certifications."
                value={masterText}
                onChange={(e) => setMasterText(e.target.value)}
                className="font-mono text-sm sm:text-sm min-h-[520px] max-h-[800px] resize-y p-2"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <span>
                  Tip: Press{" "}
                  <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground">
                    Ctrl + S
                  </kbd>{" "}
                  to save anytime.
                </span>

                <span>Do not worry much about formatting</span>
              </div>
            </div>

            {/* Bottom Action Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 mt-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => saveMutation.mutate(masterText)}
                  loading={saveMutation.isPending}
                  size="default"
                  className="bg-primary hover:bg-primary/90 text-white font-medium shadow-sm px-5"
                >
                  <Save className="mr-1.5 size-4" />
                  Save Career Profile
                </Button>
              </div>

              <Link to="/">
                <Button variant="secondary" size="default" className="font-medium gap-1.5">
                  <Wand2 className="size-4 text-muted-foreground" />
                  Go to Resume Tailor
                  <ArrowRight className="size-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        </TabsPanel>

        {/* Tab 2: Account Details */}
        <TabsPanel value="settings" className="flex flex-col gap-6 pt-2">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col gap-5">
            <h2 className="text-sm font-semibold text-foreground">Account Information</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1 rounded-xl border border-border bg-muted/20 p-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Full Name
                </span>
                <p className="text-sm font-semibold text-foreground">
                  {session?.user?.name || "Not provided"}
                </p>
              </div>

              <div className="flex flex-col gap-1 rounded-xl border border-border bg-muted/20 p-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Email Address
                </span>
                <p className="text-sm font-semibold text-foreground">{session?.user?.email}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground">Quick Links</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/credits" className="block">
                  <div className="flex items-center justify-between rounded-xl border border-border p-4 hover:bg-accent/40 transition-colors">
                    <div className="flex items-center gap-3">
                      <Coins className="size-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Credits &amp; Usage</p>
                        <p className="text-xs text-muted-foreground">
                          View transaction history and add credits
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground" />
                  </div>
                </Link>

                <Link to="/api-keys" className="block">
                  <div className="flex items-center justify-between rounded-xl border border-border p-4 hover:bg-accent/40 transition-colors">
                    <div className="flex items-center gap-3">
                      <KeyRound className="size-5 text-emerald-500" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">
                            API Keys &amp; MCP
                          </p>
                          <ExperimentalBadge text="Beta" />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Manage programmatic access and MCP server
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground" />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </TabsPanel>
      </Tabs>
    </PageLayout>
  );
}
